'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { useState } from 'react';

// Type guard for HTTP errors
function isHttpError(error: unknown): error is Error & { status: number } {
  return error instanceof Error && 'status' in error && typeof (error as Error & { status: unknown }).status === 'number';
}

function ReactQueryProvider({ children, isDev }: { children: React.ReactNode; isDev: boolean }) {
  // Create a new QueryClient instance for each app instance
  // This ensures that data is not shared between different users/sessions
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // How long data stays fresh before being considered stale (5 mins)
            staleTime: 1000 * 60 * 5,
            // SSR timers retain completed request caches. Infinity lets the request be garbage collected.
            // Keep unused browser data for 30 minutes.
            gcTime: typeof window === 'undefined' ? Infinity : 1000 * 60 * 30,
            retry: (failureCount, error) => {
              if (isHttpError(error)) {
                const status = error.status;
                if (status >= 400 && status < 500) {
                  return false;
                }
              }
              return failureCount < 3;
            },
          },
        },
      })
  );

  const devtools = isDev
    ? React.createElement(ReactQueryDevtools as React.ComponentType<Record<string, unknown>>, {
        initialIsOpen: false,
        buttonPosition: 'bottom-right',
      })
    : null;

  return React.createElement(QueryClientProvider, { client: queryClient }, children, devtools);
}

export { ReactQueryProvider };
